import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  CheckCircle,
  Clock,
  ArrowLeft,
  Home,
  RefreshCw,
  Eye,
  Search,
} from "lucide-react";
import api from "../services/apiService";

const OrderSuccess = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [debugInfo, setDebugInfo] = useState({});
  // Confetti effect
  const confettiRef = useRef(null);
  useEffect(() => {
    if (order && confettiRef.current) {
      import("canvas-confetti").then((confetti) => {
        confetti.create(confettiRef.current, { resize: true })({
          particleCount: 120,
          spread: 80,
          origin: { y: 0.6 },
        });
      });
    }
  }, [order]);

  useEffect(() => {
    const fetchOrder = async () => {
      setIsLoading(true);
      try {
        const token = localStorage.getItem("token");
        const res = await api.get(`/orders/${orderId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setOrder(res.data);
      } catch (err) {
        setOrder(null);
        setDebugInfo({
          searchingForId: orderId,
          error: err?.message,
        });
      } finally {
        setIsLoading(false);
      }
    };
    fetchOrder();
  }, [orderId]);

  const getStatusColor = (status) => {
    const colors = {
      pending: "bg-yellow-100 text-yellow-800",
      preparing: "bg-blue-100 text-blue-800",
      ready: "bg-green-100 text-green-800",
      completed: "bg-gray-100 text-gray-800",
      cancelled: "bg-red-100 text-red-800",
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: Clock,
      preparing: RefreshCw,
      ready: CheckCircle,
      completed: CheckCircle,
      cancelled: Clock,
    };
    const Icon = icons[status] || Clock;
    return <Icon className="w-4 h-4" />;
  };

  const getStatusMessage = (status) => {
    const messages = {
      pending:
        "Waiting for confirmation... Your order will be prepared once confirmed.",
      preparing:
        "Order successfully placed! Your order is being prepared by our kitchen staff.",
      ready: "Your order is ready for pickup!",
      completed: "Your order has been completed. Thank you!",
      cancelled: "Your order has been cancelled.",
    };
    return messages[status] || "Order status unknown.";
  };

  const refreshOrderStatus = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await api.get(`/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrder(res.data);
    } catch (error) {
      setOrder(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckOrderStatus = () => {
    navigate("/order-status");
  };

  const handleBackToHome = () => {
    navigate("/");
  };

  const handleBackToMenu = () => {
    navigate(-1);
  };

  if (!order) {
    if (isLoading) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-100 flex items-center justify-center p-2 sm:p-4">
          <div className="text-center w-full max-w-md bg-white/90 rounded-2xl shadow-xl p-4 sm:p-8 border border-blue-100">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6 animate-spin">
              <Clock className="w-8 h-8 sm:w-10 sm:h-10 text-blue-600" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-blue-700 mb-2 tracking-tight">
              Loading Order...
            </h1>
            <p className="text-gray-600 mb-4 sm:mb-6 text-sm sm:text-base">
              Please wait while we fetch your order details.
            </p>
          </div>
        </div>
      );
    }
    // Only show not found if not loading and no order
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-gray-100 flex items-center justify-center p-2 sm:p-4">
        <div className="text-center w-full max-w-md bg-white/90 rounded-2xl shadow-xl p-4 sm:p-8 border border-red-100">
          <div className="w-16 h-16 sm:w-20 sm:h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6 animate-pulse">
            <Clock className="w-8 h-8 sm:w-10 sm:h-10 text-red-600" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-red-700 mb-2 tracking-tight">
            Order Not Found
          </h1>
          <p className="text-gray-600 mb-4 sm:mb-6 text-sm sm:text-base">
            The order with ID{" "}
            <span className="font-semibold text-red-700">#{orderId}</span> could
            not be found. It may have been removed or the ID is incorrect.
          </p>
          {/* Debug Information */}
          <div className="bg-red-50/80 p-2 sm:p-4 rounded-lg mb-4 sm:mb-6 text-left text-xs border border-red-100 overflow-x-auto">
            <h3 className="font-bold mb-2 text-red-700">Debug Information:</h3>
            <p>
              <strong>Looking for ID:</strong> {debugInfo.searchingForId}
            </p>
            <p>
              <strong>Error:</strong> {debugInfo.error || "Not found"}
            </p>
          </div>
          <div className="space-y-2 sm:space-y-3">
            <button
              onClick={handleCheckOrderStatus}
              className="w-full flex items-center justify-center space-x-2 px-3 py-2 sm:px-4 sm:py-3 rounded-lg bg-gradient-to-r from-red-500 to-pink-400 text-white font-semibold shadow hover:from-red-600 hover:to-pink-500 transition text-sm sm:text-base"
            >
              <Search className="w-4 h-4 sm:w-5 sm:h-5" />
              <span>Check Order Status</span>
            </button>
            <button
              onClick={handleBackToHome}
              className="w-full flex items-center justify-center space-x-2 px-3 py-2 sm:px-4 sm:py-3 rounded-lg bg-white border border-gray-200 text-gray-700 font-semibold shadow hover:bg-gray-50 transition text-sm sm:text-base"
            >
              <Home className="w-4 h-4 sm:w-5 sm:h-5" />
              <span>Back to Home</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Calculate total from items if available
  const calculatedTotal =
    order.items && Array.isArray(order.items)
      ? order.items.reduce((sum, item) => sum + item.price * item.quantity, 0)
      : typeof order.total === "number"
      ? order.total
      : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-100 flex items-center justify-center p-2 sm:p-4 relative">
      {/* Confetti Canvas */}
      <canvas
        ref={confettiRef}
        className="pointer-events-none fixed inset-0 w-full h-full z-10"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          zIndex: 10,
        }}
      />
      <div className="w-full max-w-2xl z-20">
        <div className="bg-white/95 rounded-3xl shadow-2xl p-3 sm:p-6 md:p-10 border border-green-100 relative overflow-hidden">
          {/* Animated Checkmark */}
          <div className="flex flex-col items-center mb-6 sm:mb-8">
            <div className="w-16 h-16 sm:w-24 sm:h-24 bg-gradient-to-br from-green-200 to-green-100 rounded-full flex items-center justify-center mb-4 sm:mb-6 shadow-lg animate-bounce-slow relative">
              <svg
                className="w-10 h-10 sm:w-14 sm:h-14 text-green-600 animate-checkmark"
                viewBox="0 0 52 52"
              >
                <circle
                  className="stroke-current text-green-300"
                  cx="26"
                  cy="26"
                  r="25"
                  fill="none"
                  strokeWidth="3"
                />
                <path
                  className="stroke-current text-green-600"
                  fill="none"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M14 27l8 8 16-16"
                />
              </svg>
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-green-700 mb-1 sm:mb-2 tracking-tight drop-shadow">
              {order.status === "pending"
                ? "Order Received!"
                : "Order Placed Successfully!"}
            </h1>
            <p className="text-gray-600 mb-4 sm:mb-6 text-sm sm:text-base font-medium">
              {getStatusMessage(order.status)}
            </p>
          </div>
          {/* Order Status */}
          <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-3 sm:p-6 mb-6 sm:mb-8 border border-green-100/50 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 sm:mb-4 gap-2">
              <div className="flex items-center space-x-2">
                <Clock className="w-5 h-5 text-green-600" />
                <span className="text-base font-semibold text-gray-700">
                  Order ID: <span className="text-green-700">#{orderId}</span>
                </span>
              </div>
              <button
                onClick={refreshOrderStatus}
                className="p-2 text-green-500 hover:text-green-700 transition-colors"
                title="Refresh Status"
              >
                <RefreshCw className="w-5 h-5" />
              </button>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div className="flex items-center space-x-3">
                {getStatusIcon(order.status)}
                <span className="font-semibold text-gray-900">
                  Status:{" "}
                  <span className="text-green-700">
                    {order.status.charAt(0).toUpperCase() +
                      order.status.slice(1)}
                  </span>
                </span>
              </div>
              <span
                className={`px-4 py-1 rounded-full text-sm font-bold shadow ${getStatusColor(
                  order.status
                )}`}
              >
                {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
              </span>
            </div>
            <div className="mt-3 sm:mt-4 text-xs sm:text-sm text-gray-600">
              <p>
                Ordered at:{" "}
                <span className="font-medium text-gray-800">
                  {new Date(order.createdAt).toLocaleString()}
                </span>
              </p>
              {order.updatedAt !== order.createdAt && (
                <p>
                  Last updated:{" "}
                  <span className="font-medium text-gray-800">
                    {new Date(order.updatedAt).toLocaleString()}
                  </span>
                </p>
              )}
            </div>
          </div>
          {/* Order Details Toggle */}
          <div className="mb-4 sm:mb-6">
            <button
              onClick={() => setShowOrderDetails(!showOrderDetails)}
              className="w-full flex items-center justify-center space-x-2 px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-green-100 to-blue-100 text-green-800 font-semibold rounded-lg shadow hover:from-green-200 hover:to-blue-200 transition text-sm sm:text-base"
            >
              <Eye className="w-4 h-4 sm:w-5 sm:h-5" />
              <span>{showOrderDetails ? "Hide" : "Show"} Order Details</span>
            </button>
          </div>
          {/* Order Details */}
          {showOrderDetails && (
            <div className="bg-white/80 rounded-xl p-3 sm:p-6 mb-6 sm:mb-8 border border-gray-100 shadow-inner">
              <h3 className="font-bold text-base sm:text-lg text-green-700 mb-3 sm:mb-4">
                Order Details
              </h3>
              <div className="space-y-3 sm:space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-700 mb-1 sm:mb-2">
                    Customer Information
                  </h4>
                  <div className="text-xs sm:text-sm text-gray-600 space-y-1">
                    <p>
                      <strong>Name:</strong> {order.customerName}
                    </p>
                    <p>
                      <strong>Phone:</strong> {order.phone}
                    </p>
                    <p>
                      <strong>Table:</strong> {order.tableNumber}
                    </p>
                    {order.specialInstructions && (
                      <p>
                        <strong>Special Instructions:</strong>{" "}
                        {order.specialInstructions}
                      </p>
                    )}
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-700 mb-1 sm:mb-2">
                    Restaurant Information
                  </h4>
                  <div className="text-xs sm:text-sm text-gray-600 space-y-1">
                    <p>
                      <strong>Restaurant:</strong>{" "}
                      {order.restaurant?.name || "N/A"}
                    </p>
                    {order.restaurant?.address && (
                      <p>
                        <strong>Address:</strong> {order.restaurant.address}
                      </p>
                    )}
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-700 mb-1 sm:mb-2">
                    Order Items
                  </h4>
                  <div className="space-y-1 sm:space-y-2">
                    {order.items?.map((item, index) => (
                      <div
                        key={index}
                        className="flex justify-between items-center text-xs sm:text-sm"
                      >
                        <span className="text-gray-600">
                          {item.quantity}x {item.name}
                        </span>
                        <span className="font-semibold">
                          ${(item.price * item.quantity).toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="border-t pt-3 sm:pt-4">
                  <div className="flex justify-between items-center text-base sm:text-lg font-bold">
                    <span>Total:</span>
                    <span className="text-green-700">
                      ${calculatedTotal.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* Action Buttons */}
          <div className="space-y-2 sm:space-y-3">
            <button
              onClick={handleBackToMenu}
              className="w-full flex items-center justify-center space-x-2 px-3 py-2 sm:px-4 sm:py-3 rounded-lg bg-gradient-to-r from-blue-500 to-green-400 text-white font-semibold shadow hover:from-blue-600 hover:to-green-500 transition text-sm sm:text-base"
            >
              <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
              <span>Back to Menu</span>
            </button>
            <button
              onClick={handleCheckOrderStatus}
              className="w-full flex items-center justify-center space-x-2 px-3 py-2 sm:px-4 sm:py-3 rounded-lg bg-white border border-gray-200 text-green-700 font-semibold shadow hover:bg-gray-50 transition text-sm sm:text-base"
            >
              <Search className="w-4 h-4 sm:w-5 sm:h-5" />
              <span>Check Order Status</span>
            </button>
          </div>
        </div>
        <div className="mt-6 sm:mt-8 text-center">
          <p className="text-sm sm:text-base text-gray-500 font-medium">
            Thank you for choosing our restaurant!{" "}
            <span className="text-green-700">We appreciate your business.</span>
          </p>
        </div>
        {/* Custom CSS for animation */}
        <style>{`
          @keyframes checkmark {
            0% { stroke-dashoffset: 48; }
            60% { stroke-dashoffset: 0; }
            100% { stroke-dashoffset: 0; }
          }
          .animate-checkmark path {
            stroke-dasharray: 48;
            stroke-dashoffset: 48;
            animation: checkmark 1s cubic-bezier(0.77,0,0.175,1) 0.2s forwards;
          }
          @keyframes bounce-slow {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-10px); }
          }
          .animate-bounce-slow {
            animation: bounce-slow 2.5s infinite;
          }
        `}</style>
      </div>
    </div>
  );
};

export default OrderSuccess;
